import { Grid, Box, Typography, Chip } from '@mui/material';
import { useTheme } from '@/styles/use-finance-theme';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { NavLink } from 'react-router-dom';
import { SurfaceCard } from '../../data-display/SurfaceCard';
import { resolveIcon } from '../../../runtime/icons';
import { getRouteById } from '../../../routes/registry';
import { useAppI18n } from '../../../i18n/use-app-i18n';
import { appShadow } from '../../../styles/tokens/semantic';
import { accentColorFor, buildCardTargetPath, getAvailabilityPresentation, isCardNavigable } from './hub-utils';
import { isWipFeaturesVisible } from '../../../config/feature-flags';
import type { NavigationCardDescriptor } from '@finance/contracts';

export interface HubCardProps {
  card: NavigationCardDescriptor;
}

export function HubCard({ card }: HubCardProps) {
  const { text, runtimeText } = useAppI18n();
  const theme = useTheme();
  const route = getRouteById(card.routeId);
  const navigable = isCardNavigable(card);
  const targetPath = route ? buildCardTargetPath(route.path, card.routeIntent) : undefined;
  const Icon = resolveIcon(card.iconKey);
  const color = accentColorFor(card);
  const presentation = getAvailabilityPresentation(card.availability);
  const StateIcon = presentation.icon;
  const disabledReason = card.disabledReasonKey ? text(card.disabledReasonKey) : undefined;
  const showWipBadge = isWipFeaturesVisible() && card.wip;

  const cardContent = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
        <Box
          className="hub-card-icon-bg"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: theme.financeLayout.cardLayout.borderRadius ?? 2,
            bgcolor: presentation.muted ? 'action.disabledBackground' : 'action.hover',
            color: presentation.muted ? 'text.disabled' : 'primary.main',
            transition: 'background-color 0.2s ease, color 0.2s ease',
            flexShrink: 0,
          }}
        >
          {presentation.muted ? (
            <StateIcon fontSize="small" />
          ) : Icon ? (
            <Icon fontSize="small" />
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {presentation.labelKey && (
            <Chip
              label={runtimeText(presentation.labelKey)}
              size="small"
              variant="outlined"
              data-testid={`hub-card-status-${card.id}`}
              sx={{ height: 24, fontWeight: 600, color: 'text.secondary' }}
            />
          )}
          {showWipBadge && (
            <Chip
              label={text('common.inDevelopment')}
              size="small"
              variant="outlined"
              data-testid={`hub-card-wip-${card.id}`}
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 500,
                color: 'text.secondary',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            />
          )}
          {navigable && (
            <ArrowForwardIcon
              className="hub-card-arrow"
              sx={{
                fontSize: 18,
                color: 'text.disabled',
                opacity: 0,
                transform: 'translateX(-4px)',
                transition: 'opacity 0.2s ease, transform 0.2s ease',
              }}
            />
          )}
        </Box>
      </Box>

      <Typography variant="h6" component="h3" sx={{ mb: 0.5, fontSize: '1rem', lineHeight: 1.35 }}>
        {text(card.titleKey)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {!navigable && disabledReason
          ? disabledReason
          : card.descriptionKey
            ? text(card.descriptionKey)
            : ''}
      </Typography>
    </>
  );

  const sharedSx = {
    textDecoration: 'none',
    color: 'inherit',
    height: '100%',
    minHeight: 152,
    overflow: 'hidden',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
    '&:focus-visible': {
      outline: '2px solid',
      outlineColor: `${color}.main`,
      outlineOffset: 2,
    },
  } as const;

  const hoverSx = {
    borderColor: 'primary.main',
    boxShadow: appShadow.cardHover,
    transform: 'translateY(-1px)',
    '& .hub-card-arrow': { opacity: 1, transform: 'translateX(0)' },
    '& .hub-card-icon-bg': { bgcolor: 'primary.main', color: 'primary.contrastText' },
  };

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} key={card.id} sx={{ display: 'flex' }}>
      {navigable && targetPath ? (
        <SurfaceCard
          component={NavLink}
          to={targetPath}
          data-target-path={targetPath}
          data-card-id={card.id}
          data-testid={`hub-card-${card.id}`}
          data-card-navigable="true"
          data-card-availability={card.availability}
          surfaceRole="interactive"
          padding="standard"
          accentColor={color}
          hoverSx={hoverSx}
          sx={{ ...sharedSx, width: '100%' }}
        >
          {cardContent}
        </SurfaceCard>
      ) : (
        <SurfaceCard
          aria-disabled="true"
          data-card-id={card.id}
          data-testid={`hub-card-${card.id}`}
          data-card-navigable="false"
          data-card-availability={card.availability}
          data-missing-route={!route?.path}
          surfaceRole="standard"
          padding="standard"
          accentColor={color}
          sx={{
            ...sharedSx,
            width: '100%',
            cursor: 'not-allowed',
            opacity: presentation.muted ? 0.78 : 0.9,
          }}
        >
          {cardContent}
        </SurfaceCard>
      )}
    </Grid>
  );
}
